/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { BusinessModel } from 'src/graphql/generated/graphql.generated';
import {
  HoursOfOperationRecord,
  HoursOfOperationProps,
} from 'src/store/generated/hours-of-operation-record.generated';
import {
  IdNameListItemRecord,
  IdNameListItemProps,
} from 'src/store/generated/id-name-list-item-record.generated';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface BaseProps {
  id: string;
  name: string;
  abbreviation: string;
  locationId: string;
  plannedOperationsStart: Date | null;
  operationsStarted: Date | null;
  operationsTerminated: Date | null;
  dateOfMaturity: Date | null;
  primaryPhoneNumber: string | null;
  hoursOfOperation: HoursOfOperationProps;
  businessModel: BusinessModel;
  estimatedLiftoffTime: number | null;
  licenseNumber: string | null;
  enableFlightLogDutyLog: boolean | null;
  dispatchBaseId: string | null;
  reportBaseId: string | null;
  aviationCompanyId: string | null;
  workdayBaseId: string | null;
  gmrWorkdayLocationId: string | null;
  dateLastUpdatedFromGMRWorkday: Date | null;
  pilotPhoneNumber: string | null;
  crewPhoneNumber1: string | null;
  crewPhoneNumber2: string | null;
  brandName: string | null;
  medicalFacilityId: string | null;
  communicationCenterCustomIdentifiers: List<IdNameListItemProps> | null;
  additionalCompanies: List<IdListItemProps> | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class BaseRecord extends Record<BaseProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  locationId: null as any,
  plannedOperationsStart: null as any,
  operationsStarted: null as any,
  operationsTerminated: null as any,
  dateOfMaturity: null as any,
  primaryPhoneNumber: null as any,
  hoursOfOperation: null as any,
  businessModel: null as any,
  estimatedLiftoffTime: null as any,
  licenseNumber: null as any,
  enableFlightLogDutyLog: null as any,
  dispatchBaseId: null as any,
  reportBaseId: null as any,
  aviationCompanyId: null as any,
  workdayBaseId: null as any,
  gmrWorkdayLocationId: null as any,
  dateLastUpdatedFromGMRWorkday: null as any,
  pilotPhoneNumber: null as any,
  crewPhoneNumber1: null as any,
  crewPhoneNumber2: null as any,
  brandName: null as any,
  medicalFacilityId: null as any,
  communicationCenterCustomIdentifiers: null as any,
  additionalCompanies: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    locationId: string;
    plannedOperationsStart: string | number | Date | null;
    operationsStarted: string | number | Date | null;
    operationsTerminated: string | number | Date | null;
    dateOfMaturity: string | number | Date | null;
    primaryPhoneNumber: string | null;
    hoursOfOperation: any;
    businessModel: BusinessModel;
    estimatedLiftoffTime: number | null;
    licenseNumber: string | null;
    enableFlightLogDutyLog: boolean | null;
    dispatchBaseId: string | null;
    reportBaseId: string | null;
    aviationCompanyId: string | null;
    workdayBaseId: string | null;
    gmrWorkdayLocationId: string | null;
    dateLastUpdatedFromGMRWorkday: string | number | Date | null;
    pilotPhoneNumber: string | null;
    crewPhoneNumber1: string | null;
    crewPhoneNumber2: string | null;
    brandName: string | null;
    medicalFacilityId: string | null;
    communicationCenterCustomIdentifiers: any;
    additionalCompanies: any;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): BaseRecord {
    if (json == null) {
      throw new Error('BaseRecord.fromJS: parameter cannot be null');
    }
    return new BaseRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'BaseJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'BaseJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'BaseJson.abbreviation'),
      locationId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'locationId', '00000000-0000-0000-0000-000000000000'),
        'BaseJson.locationId',
      ),
      plannedOperationsStart:
        json.plannedOperationsStart != null ? new Date(json.plannedOperationsStart) : null,
      operationsStarted: json.operationsStarted != null ? new Date(json.operationsStarted) : null,
      operationsTerminated:
        json.operationsTerminated != null ? new Date(json.operationsTerminated) : null,
      dateOfMaturity: json.dateOfMaturity != null ? new Date(json.dateOfMaturity) : null,
      primaryPhoneNumber: json.primaryPhoneNumber,
      hoursOfOperation: new HoursOfOperationRecord(
        getFromOrThrow(json, (j) => j.hoursOfOperation, 'BaseJson.hoursOfOperation'),
      ),
      businessModel: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'businessModel', BusinessModel.Unspecified),
        'BaseJson.businessModel',
      ),
      estimatedLiftoffTime: json.estimatedLiftoffTime,
      licenseNumber: json.licenseNumber,
      enableFlightLogDutyLog: json.enableFlightLogDutyLog,
      dispatchBaseId: json.dispatchBaseId,
      reportBaseId: json.reportBaseId,
      aviationCompanyId: json.aviationCompanyId,
      workdayBaseId: json.workdayBaseId,
      gmrWorkdayLocationId: json.gmrWorkdayLocationId,
      dateLastUpdatedFromGMRWorkday:
        json.dateLastUpdatedFromGMRWorkday != null
          ? new Date(json.dateLastUpdatedFromGMRWorkday)
          : null,
      pilotPhoneNumber: json.pilotPhoneNumber,
      crewPhoneNumber1: json.crewPhoneNumber1,
      crewPhoneNumber2: json.crewPhoneNumber2,
      brandName: json.brandName,
      medicalFacilityId: json.medicalFacilityId,
      communicationCenterCustomIdentifiers:
        json.communicationCenterCustomIdentifiers != null
          ? List<IdNameListItemRecord>(
              json.communicationCenterCustomIdentifiers.map(
                (o: any) => new IdNameListItemRecord(o),
              ),
            )
          : null,
      additionalCompanies:
        json.additionalCompanies != null
          ? List<IdListItemRecord>(
              json.additionalCompanies.map((o: any) => new IdListItemRecord(o)),
            )
          : null,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'BaseJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    locationId: string;
    plannedOperationsStart: string | number | Date | null;
    operationsStarted: string | number | Date | null;
    operationsTerminated: string | number | Date | null;
    dateOfMaturity: string | number | Date | null;
    primaryPhoneNumber: string | null;
    hoursOfOperation: any;
    businessModel: BusinessModel;
    estimatedLiftoffTime: number | null;
    licenseNumber: string | null;
    enableFlightLogDutyLog: boolean | null;
    dispatchBaseId: string | null;
    reportBaseId: string | null;
    aviationCompanyId: string | null;
    workdayBaseId: string | null;
    gmrWorkdayLocationId: string | null;
    dateLastUpdatedFromGMRWorkday: string | number | Date | null;
    pilotPhoneNumber: string | null;
    crewPhoneNumber1: string | null;
    crewPhoneNumber2: string | null;
    brandName: string | null;
    medicalFacilityId: string | null;
    communicationCenterCustomIdentifiers: any;
    additionalCompanies: any;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [BaseRecord, Error[]] {
    if (json == null) {
      throw new Error('BaseRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new BaseRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'BaseJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'BaseJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'BaseJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'locationId', '00000000-0000-0000-0000-000000000000'),
            'BaseJson.locationId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      plannedOperationsStart: tryOrDefault(
        () => (json.plannedOperationsStart != null ? new Date(json.plannedOperationsStart) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationsStarted: tryOrDefault(
        () => (json.operationsStarted != null ? new Date(json.operationsStarted) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      operationsTerminated: tryOrDefault(
        () => (json.operationsTerminated != null ? new Date(json.operationsTerminated) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateOfMaturity: tryOrDefault(
        () => (json.dateOfMaturity != null ? new Date(json.dateOfMaturity) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      primaryPhoneNumber: tryOrDefault(
        () => json.primaryPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      hoursOfOperation: tryOrDefault(
        () =>
          new HoursOfOperationRecord(
            getFromOrThrow(json, (j) => j.hoursOfOperation, 'BaseJson.hoursOfOperation'),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      businessModel: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'businessModel', BusinessModel.Unspecified),
            'BaseJson.businessModel',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedLiftoffTime: tryOrDefault(
        () => json.estimatedLiftoffTime,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      licenseNumber: tryOrDefault(
        () => json.licenseNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      enableFlightLogDutyLog: tryOrDefault(
        () => json.enableFlightLogDutyLog,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dispatchBaseId: tryOrDefault(
        () => json.dispatchBaseId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reportBaseId: tryOrDefault(
        () => json.reportBaseId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      aviationCompanyId: tryOrDefault(
        () => json.aviationCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      workdayBaseId: tryOrDefault(
        () => json.workdayBaseId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gmrWorkdayLocationId: tryOrDefault(
        () => json.gmrWorkdayLocationId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateLastUpdatedFromGMRWorkday: tryOrDefault(
        () =>
          json.dateLastUpdatedFromGMRWorkday != null
            ? new Date(json.dateLastUpdatedFromGMRWorkday)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      pilotPhoneNumber: tryOrDefault(
        () => json.pilotPhoneNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      crewPhoneNumber1: tryOrDefault(
        () => json.crewPhoneNumber1,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      crewPhoneNumber2: tryOrDefault(
        () => json.crewPhoneNumber2,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      brandName: tryOrDefault(
        () => json.brandName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalFacilityId: tryOrDefault(
        () => json.medicalFacilityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterCustomIdentifiers: tryOrDefault(
        () =>
          json.communicationCenterCustomIdentifiers != null
            ? List<IdNameListItemRecord>(
                json.communicationCenterCustomIdentifiers.map(
                  (o: any) => new IdNameListItemRecord(o),
                ),
              )
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      additionalCompanies: tryOrDefault(
        () =>
          json.additionalCompanies != null
            ? List<IdListItemRecord>(
                json.additionalCompanies.map((o: any) => new IdListItemRecord(o)),
              )
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'BaseJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: BaseProps) {
    super(props);
    if (props == null) {
      throw new Error('BaseRecord constructor parameter cannot be null');
    }
  }
}

export abstract class BaseViewModelBase implements BaseProps {
  protected readonly _props: BaseProps;
  private _json?: BaseProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get locationId(): string {
    return this._props.locationId;
  }
  get plannedOperationsStart(): Date | null {
    return this._props.plannedOperationsStart;
  }
  get operationsStarted(): Date | null {
    return this._props.operationsStarted;
  }
  get operationsTerminated(): Date | null {
    return this._props.operationsTerminated;
  }
  get dateOfMaturity(): Date | null {
    return this._props.dateOfMaturity;
  }
  get primaryPhoneNumber(): string | null {
    return this._props.primaryPhoneNumber;
  }
  get hoursOfOperation(): HoursOfOperationProps {
    return this._props.hoursOfOperation;
  }
  get businessModel(): BusinessModel {
    return this._props.businessModel;
  }
  get estimatedLiftoffTime(): number | null {
    return this._props.estimatedLiftoffTime;
  }
  get licenseNumber(): string | null {
    return this._props.licenseNumber;
  }
  get enableFlightLogDutyLog(): boolean | null {
    return this._props.enableFlightLogDutyLog;
  }
  get dispatchBaseId(): string | null {
    return this._props.dispatchBaseId;
  }
  get reportBaseId(): string | null {
    return this._props.reportBaseId;
  }
  get aviationCompanyId(): string | null {
    return this._props.aviationCompanyId;
  }
  get workdayBaseId(): string | null {
    return this._props.workdayBaseId;
  }
  get gmrWorkdayLocationId(): string | null {
    return this._props.gmrWorkdayLocationId;
  }
  get dateLastUpdatedFromGMRWorkday(): Date | null {
    return this._props.dateLastUpdatedFromGMRWorkday;
  }
  get pilotPhoneNumber(): string | null {
    return this._props.pilotPhoneNumber;
  }
  get crewPhoneNumber1(): string | null {
    return this._props.crewPhoneNumber1;
  }
  get crewPhoneNumber2(): string | null {
    return this._props.crewPhoneNumber2;
  }
  get brandName(): string | null {
    return this._props.brandName;
  }
  get medicalFacilityId(): string | null {
    return this._props.medicalFacilityId;
  }
  get communicationCenterCustomIdentifiers(): List<IdNameListItemProps> | null {
    return this._props.communicationCenterCustomIdentifiers;
  }
  get additionalCompanies(): List<IdListItemProps> | null {
    return this._props.additionalCompanies;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCommunity() {
    return this._props.businessModel === BusinessModel.Community;
  }
  public get isGround() {
    return this._props.businessModel === BusinessModel.Ground;
  }
  public get isTraditional() {
    return this._props.businessModel === BusinessModel.Traditional;
  }
  public get isUnspecified() {
    return this._props.businessModel === BusinessModel.Unspecified;
  }

  public isAnyBusinessModel(...businessModel: BusinessModel[]) {
    return businessModel.includes(this._props.businessModel);
  }

  constructor(props: BaseProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        locationId: this._props.locationId,
        plannedOperationsStart: this._props.plannedOperationsStart,
        operationsStarted: this._props.operationsStarted,
        operationsTerminated: this._props.operationsTerminated,
        dateOfMaturity: this._props.dateOfMaturity,
        primaryPhoneNumber: this._props.primaryPhoneNumber,
        hoursOfOperation: this._props.hoursOfOperation,
        businessModel: this._props.businessModel,
        estimatedLiftoffTime: this._props.estimatedLiftoffTime,
        licenseNumber: this._props.licenseNumber,
        enableFlightLogDutyLog: this._props.enableFlightLogDutyLog,
        dispatchBaseId: this._props.dispatchBaseId,
        reportBaseId: this._props.reportBaseId,
        aviationCompanyId: this._props.aviationCompanyId,
        workdayBaseId: this._props.workdayBaseId,
        gmrWorkdayLocationId: this._props.gmrWorkdayLocationId,
        dateLastUpdatedFromGMRWorkday: this._props.dateLastUpdatedFromGMRWorkday,
        pilotPhoneNumber: this._props.pilotPhoneNumber,
        crewPhoneNumber1: this._props.crewPhoneNumber1,
        crewPhoneNumber2: this._props.crewPhoneNumber2,
        brandName: this._props.brandName,
        medicalFacilityId: this._props.medicalFacilityId,
        communicationCenterCustomIdentifiers: this._props.communicationCenterCustomIdentifiers,
        additionalCompanies: this._props.additionalCompanies,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllBaseEntities = (state: AppState) => state.entityStores.base.entities;

export const selectBaseById = (id: string) => (state: AppState) =>
  state.entityStores.base.entities.get(id);

export const selectBaseMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.base.meta.get(id);

export const selectBaseAndMetadataById = (id: string) =>
  createSelector(selectBaseById(id), selectBaseMetadataById(id), (base, meta) => ({ base, meta }));

export function selectBaseEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllBaseEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, BaseRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectBaseEntitiesByName(name: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectBaseEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectBaseEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}

export function selectBaseEntitiesByPlannedOperationsStart(plannedOperationsStart: Date) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.plannedOperationsStart === plannedOperationsStart).valueSeq(),
  );
}

export function selectBaseEntitiesByOperationsStarted(operationsStarted: Date) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.operationsStarted === operationsStarted).valueSeq(),
  );
}

export function selectBaseEntitiesByOperationsTerminated(operationsTerminated: Date) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.operationsTerminated === operationsTerminated).valueSeq(),
  );
}

export function selectBaseEntitiesByDateOfMaturity(dateOfMaturity: Date) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.dateOfMaturity === dateOfMaturity).valueSeq(),
  );
}

export function selectBaseEntitiesByPrimaryPhoneNumber(primaryPhoneNumber: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.primaryPhoneNumber === primaryPhoneNumber).valueSeq(),
  );
}

export function selectBaseEntitiesByBusinessModel(businessModel: BusinessModel) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.businessModel === businessModel).valueSeq(),
  );
}

export function selectBaseEntitiesByEstimatedLiftoffTime(estimatedLiftoffTime: number) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.estimatedLiftoffTime === estimatedLiftoffTime).valueSeq(),
  );
}

export function selectBaseEntitiesByLicenseNumber(licenseNumber: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.licenseNumber === licenseNumber).valueSeq(),
  );
}

export function selectBaseEntitiesByEnableFlightLogDutyLog(enableFlightLogDutyLog: boolean) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.enableFlightLogDutyLog === enableFlightLogDutyLog).valueSeq(),
  );
}

export function selectBaseEntitiesByDispatchBaseId(dispatchBaseId: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.dispatchBaseId === dispatchBaseId).valueSeq(),
  );
}

export function selectBaseEntitiesByReportBaseId(reportBaseId: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.reportBaseId === reportBaseId).valueSeq(),
  );
}

export function selectBaseEntitiesByAviationCompanyId(aviationCompanyId: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.aviationCompanyId === aviationCompanyId).valueSeq(),
  );
}

export function selectBaseEntitiesByWorkdayBaseId(workdayBaseId: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.workdayBaseId === workdayBaseId).valueSeq(),
  );
}

export function selectBaseEntitiesByGmrWorkdayLocationId(gmrWorkdayLocationId: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.gmrWorkdayLocationId === gmrWorkdayLocationId).valueSeq(),
  );
}

export function selectBaseEntitiesByDateLastUpdatedFromGMRWorkday(
  dateLastUpdatedFromGMRWorkday: Date,
) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities
      .filter((e) => e.dateLastUpdatedFromGMRWorkday === dateLastUpdatedFromGMRWorkday)
      .valueSeq(),
  );
}

export function selectBaseEntitiesByPilotPhoneNumber(pilotPhoneNumber: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.pilotPhoneNumber === pilotPhoneNumber).valueSeq(),
  );
}

export function selectBaseEntitiesByCrewPhoneNumber1(crewPhoneNumber1: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.crewPhoneNumber1 === crewPhoneNumber1).valueSeq(),
  );
}

export function selectBaseEntitiesByCrewPhoneNumber2(crewPhoneNumber2: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.crewPhoneNumber2 === crewPhoneNumber2).valueSeq(),
  );
}

export function selectBaseEntitiesByBrandName(brandName: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.brandName === brandName).valueSeq(),
  );
}

export function selectBaseEntitiesByMedicalFacilityId(medicalFacilityId: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.medicalFacilityId === medicalFacilityId).valueSeq(),
  );
}

export function selectBaseEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllBaseEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
